import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { User, AuthProviderInfo, UserRole } from '@asat/shared';

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null);
  const providers = ref<AuthProviderInfo[]>([]);
  const loading = ref(true);
  const error = ref<string | null>(null);

  const isAuthenticated = computed(() => user.value !== null);
  const isAdmin = computed(() => {
    const role = user.value?.role;
    return role === 'super_admin' || role === 'admin';
  });
  const canEdit = computed(() => {
    const role = user.value?.role;
    return role === 'super_admin' || role === 'admin' || role === 'editor';
  });
  const userRole = computed<UserRole | null>(() => user.value?.role ?? null);

  async function fetchProviders() {
    try {
      const res = await fetch('/api/auth/providers');
      if (res.ok) {
        providers.value = await res.json();
      }
    } catch (err) {
      console.error('Failed to fetch auth providers:', err);
    }
  }

  async function fetchUser() {
    loading.value = true;
    error.value = null;
    try {
      const res = await fetch('/api/auth/me', { credentials: 'include' });
      if (res.ok) {
        user.value = await res.json();
      } else {
        user.value = null;
      }
    } catch {
      user.value = null;
    } finally {
      loading.value = false;
    }
  }

  function login(provider: string) {
    window.location.href = `/api/auth/login/${provider}`;
  }

  async function logout() {
    try {
      await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
    } finally {
      user.value = null;
    }
  }

  return {
    user,
    providers,
    loading,
    error,
    isAuthenticated,
    isAdmin,
    canEdit,
    userRole,
    fetchProviders,
    fetchUser,
    login,
    logout,
  };
});
